/** Utility functions for the namespace feature. */

import { ResourceOwner, ResourceOwnerType } from 'src/generated/graphql';
import { formatDayMonthYear } from 'src/common/utils/dateFormatters';
import { getStage, Stage } from 'src/components/common/util/StageHelpers';

/**
 * Bindles has two live environments: prod (bindles.amazon.com) and gamma
 * (bindles-gamma.amazon.com). Non-prod LangHub stages register their bindle
 * resources in the gamma Bindles environment, so linking a beta/gamma/test
 * resource id to prod Bindles would 404 — and vice versa.
 */
const BINDLES_PROD_ORIGIN = 'https://bindles.amazon.com';
const BINDLES_GAMMA_ORIGIN = 'https://bindles-gamma.amazon.com';

/**
 * Link to a bindle resource page in the Bindles environment matching the
 * running stage: prod → bindles.amazon.com, everything else →
 * bindles-gamma.amazon.com.
 */
export function buildBindleHref(
  bindleResourceId?: string | null,
): string | undefined {
  if (!bindleResourceId) return undefined;
  const origin =
    getStage() === Stage.Prod ? BINDLES_PROD_ORIGIN : BINDLES_GAMMA_ORIGIN;
  return `${origin}/resource/${bindleResourceId}`;
}

/**
 * Page for the owner itself, or undefined when the owner is absent or of a kind
 * that has no page to link to.
 */
function resolveOwnerHref(owner?: ResourceOwner | null): string | undefined {
  if (!owner?.ownerId) return undefined;
  const ownerId = owner.ownerId;
  switch (owner.ownerType) {
    case ResourceOwnerType.Team:
      return `https://access.a2z.com/group-management/team/${encodeURIComponent(ownerId)}`;
    case ResourceOwnerType.Individual:
      return `https://phonetool.amazon.com/users/${encodeURIComponent(ownerId)}`;
    default:
      return undefined;
  }
}

/**
 * Link target for a namespace's owner. Falls back to the namespace's Bindle
 * resource, which also shows ownership, so the Owner row is never a dead link.
 */
export function buildOwnerHref(
  owner: ResourceOwner | null | undefined,
  bindleHref?: string,
): string | undefined {
  return resolveOwnerHref(owner) ?? bindleHref;
}

/**
 * Format an epoch-milliseconds timestamp to a human-readable date string
 * ("DD Mon YYYY", e.g. "02 Aug 2026"). All LangHub API timestamps are epoch
 * milliseconds.
 *
 * Shares `formatDayMonthYear`, so dates read identically everywhere, but keeps
 * the em dash for a missing value: here an absent date is unknown, rather than
 * the definite 'NA' the Track tables mean.
 */
export function formatEpochMillisDate(epochMillis?: number | null): string {
  if (epochMillis == null) return '—';
  return formatDayMonthYear(new Date(epochMillis));
}
